/* eslint-disable import-extensions/file-extension-required */
import {
	createProductSpecificAnalyticsIntegrationPayload,
	ErrorType,
	Severity,
	Product,
	Team,
} from '@atlassian/growth-commons-error-analytics';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';

import { addAtlOriginToUrl } from '../../utils/addAtlOriginToUrl.ts';
import { useConfluenceTransactionEndOfTrial } from '../is-billing-eligible-end-of-trial/useConfluenceTransactionEndOfTrial.ts';

import type {
	ConfluencePricingEndOfTrialPremiumToStandardQuery$data,
	ConfluencePricingEndOfTrialPremiumToStandardQuery$variables,
} from './__generated__/ConfluencePricingEndOfTrialPremiumToStandardQuery.graphql.ts';
import { ConfluencePricingEndOfTrialPremiumToStandardQuery as PricingQuery } from './ConfluencePricingEndOfTrialPremiumToStandardQuery.ts';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { processError } from '../../utils/processError';

type usePricingEndOfTrialData = {
	experienceUrl: string;
	standardPrice: number;
	premiumPrice: number;
	userCanAddPaymentDetails: boolean;
};

export function usePricingEOTPremiumToStandardQuery(skip?: boolean): {
	data: usePricingEndOfTrialData | null;
	loading: boolean;
	error?: Error;
} {
	const { cloudId, isLoggedIn, edition } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const canSkipTransactionQuery =
		skip || !cloudId || !isLoggedIn || !edition || edition !== ConfluenceEdition.PREMIUM;

	const {
		isEligible: transactionAccountEligible,
		loading: transactionAccountLoading,
		error: transactionAccountError,
	} = useConfluenceTransactionEndOfTrial(canSkipTransactionQuery, cloudId);

	const canSkipQuery =
		canSkipTransactionQuery ||
		transactionAccountLoading ||
		!!transactionAccountError ||
		!transactionAccountEligible;

	const { data, error, loading } = useQueryInterop<
		ConfluencePricingEndOfTrialPremiumToStandardQuery$data,
		ConfluencePricingEndOfTrialPremiumToStandardQuery$variables
	>(PricingQuery, {
		variables: {
			cloudId,
			productKey: 'confluence.ondemand',
		},
		skip: canSkipQuery,
		fetchPolicy: 'cache-first', // for SSR'ing purposes
		interopApolloAggClient: true,
		errorPolicy: 'all',
	});

	const processedError = processError(error);

	if (processedError) {
		const eventPayload = createProductSpecificAnalyticsIntegrationPayload({
			product: Product.CONFLUENCE,
			team: Team.GROWTH_LUNA,
			experimentMeta: {
				experimentKey: 'twcg-530_confluence_ai_in_editor_eot',
			},
			errorType: ErrorType.GRAPHQL,
			errorMessage: processedError?.message,
			severity: Severity.MEDIUM,
			source: 'usePricingEOTPremiumToStandardQuery',
			additionalAttributes: {
				cloudId,
			},
		});
		createAnalyticsEvent(eventPayload).fire();
	}

	const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;

	const isAvailableToUser =
		entitlement?.experienceCapabilities?.changeOfferingV2?.isAvailableToUser ?? false;

	if (canSkipQuery || !entitlement || !isAvailableToUser) {
		return {
			data: null,
			loading,
			error: processedError,
		};
	}

	const experienceUrl = addAtlOriginToUrl(
		entitlement?.experienceCapabilities?.changeOfferingV2?.experienceUrl ?? '',
	);
	let standardEditionPrice;
	let premiumEditionPrice;

	if (!canSkipQuery && entitlement) {
		const premiumEditionOrder = entitlement?.subscription?.trial;
		premiumEditionPrice = premiumEditionOrder?.listPriceEstimates?.find(
			(item) => item?.item?.chargeElement === 'user',
		)?.averageAmountPerUnit;

		const standardEditionOrder = entitlement?.standardPrice?.find(
			(orderItem) =>
				orderItem?.listPriceForOrderWithDefaults &&
				orderItem?.listPriceForOrderWithDefaults?.length > 0,
		);

		standardEditionPrice = standardEditionOrder?.listPriceForOrderWithDefaults?.find(
			(item) => item?.item?.chargeElement === 'user',
		)?.averageAmountPerUnit;
	}

	return {
		loading,
		error: processedError,
		data: {
			userCanAddPaymentDetails: transactionAccountEligible,
			experienceUrl,
			standardPrice: standardEditionPrice ? standardEditionPrice / 100 : 6.7,
			premiumPrice: premiumEditionPrice ? premiumEditionPrice / 100 : 13.2,
		},
	};
}
